import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, CalendarCheck, Trophy, Medal, Handshake } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { SITE_COPY, whatsappLink } from "@/lib/siteContent";
import { fetchAgentProfile } from "@/lib/publicData";

const CREDENTIAL_ICONS = { trophy: Trophy, medal: Medal, handshake: Handshake } as const;

export function AgentBrandBand() {
  const { lang } = useI18n();
  const c = SITE_COPY[lang].brand;
  const { data: agent } = useQuery({ queryKey: ["agent-profile"], queryFn: fetchAgentProfile });
  const photo = agent?.photo_url;
  const name = agent?.full_name ?? c.name;

  return (
    <section className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
      <div className="grid gap-10 md:grid-cols-[1fr_0.8fr] md:items-center md:gap-16">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">{c.eyebrow}</p>
          <h2 className="mt-4 font-serif text-4xl leading-[1.05] text-foreground md:text-6xl">{name}</h2>
          <p className="mt-5 max-w-lg text-base text-muted-foreground md:text-lg">{c.tagline}</p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/$operation/$type"
              params={{ operation: "venta", type: "casas" }}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 text-sm font-semibold uppercase tracking-wider text-primary-foreground shadow-soft transition hover:brightness-110"
            >
              {c.ctaProperties} <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href={whatsappLink(lang)}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex items-center gap-2 rounded-lg border border-border px-6 py-3 text-sm font-semibold uppercase tracking-wider text-foreground transition hover:bg-secondary"
            >
              <CalendarCheck className="h-4 w-4" /> {c.ctaMeeting}
            </a>
          </div>

          <ul className="mt-8 grid gap-2 border-t border-border pt-6 text-sm text-muted-foreground">
            {c.credentials.map((cr) => {
              const Icon = CREDENTIAL_ICONS[cr.icon];
              return (
                <li key={cr.label} className="flex items-center gap-2">
                  <Icon className="h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
                  <span className="uppercase tracking-wide text-xs font-medium">{cr.label}</span>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="mx-auto w-full max-w-sm md:max-w-none">
          <div className="aspect-[4/5] overflow-hidden rounded-2xl bg-secondary shadow-card">
            {photo ? (
              <img
                src={photo}
                alt={name}
                loading="lazy"
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="grid h-full w-full place-items-center font-serif text-8xl text-accent/60">
                {name.charAt(0)}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
